import {
  getClosedSessions,
  getProvider,
  reopenSession,
  deleteSession,
  getDeletedStoryBibles,
  restoreStoryBible,
  purgeStoryBible,
} from "../../core/storage";
import { getStrings } from "../../core/i18n";
import { exportSessionToFile } from "../exportChat";
import { exportStoryBibleToFile } from "../exportStoryBible";
import { escapeHtml, escapeAttr } from "./domUtils";

export interface SessionHistoryHandlers {
  onBackToChat: () => void;
}

// Which side of the toggle is showing — module-level so it survives this screen's own
// re-renders (e.g. after a Purge) the same way chat.ts's topbarCollapsed survives renderChat's,
// but resets to "chats" on a fresh app load rather than being persisted to storage; there's no
// expectation this needs to survive closing NovelChat entirely.
let historyTab: "chats" | "storyBibles" = "chats";

// Bulk select state, 2026-08-13 — checkbox-driven multi-select on either tab, alongside each
// row's existing single-item Recover/Purge. Module-level for the same reason historyTab is
// (survives this screen's own re-renders); reset via resetBulkSelectionState() on every tab
// switch so stale selections/confirmations from one tab never resurface on the other. Two
// independent sets (not one shared one) since a chat id and a Story Bible id could theoretically
// collide in value space, and the two tabs act on entirely different storage collections anyway.
// bulkPurgeConfirmChats/Bibles mirrors the per-row confirmingPurgeId's inline-confirm pattern —
// "selected" vs "all" so one confirm bar's text/action can serve both bulk buttons.
let selectedClosedChatIds = new Set<string>();
let selectedDeletedBibleIds = new Set<string>();
let bulkPurgeConfirmChats: "selected" | "all" | null = null;
let bulkPurgeConfirmBibles: "selected" | "all" | null = null;

function resetBulkSelectionState(): void {
  selectedClosedChatIds.clear();
  selectedDeletedBibleIds.clear();
  bulkPurgeConfirmChats = null;
  bulkPurgeConfirmBibles = null;
}

// Lists deleted (not purged) chats, with actions
// to recover or permanently purge, sharing this one screen with a second section for deleted
// Story Bibles (Recover/Purge/Export) behind a toggle. The two sides share this screen (rather
// than each getting a top-level route of their own) because they're the same underlying idea —
// this app's existing two-tier delete model, soft (the × on a chat's tab / Delete on the Story
// Bible list) then permanent (this screen's own Purge on either side) — see storage.ts's
// closeSession/deleteStoryBible for where each side's soft half actually happens. **Terminology
// note (2026-08-13)**: the × on a chat's tab was originally labeled "Close"/"Reopen" — relabeled
// to "Delete"/"Recover" to read identically to Story Bible's own soft-delete/recover wording, per
// explicit request. The underlying mechanism is unchanged (still `ChatSession.state` "open" ↔
// "closed", still `closeSession`/`reopenSession` in storage.ts, still fully reversible with no
// data loss) — only the displayed words and this screen's own DOM wiring (data-action names,
// i18n keys) changed, not the state model or its serialized values, since renaming those would
// need a migration for already-saved sessions and isn't what was actually asked for.
export function renderSessionHistory(
  container: HTMLElement,
  handlers: SessionHistoryHandlers,
  confirmingPurgeId: string | null = null
): void {
  const s = getStrings();
  const toggleHtml = `
    <div class="history-tab-toggle">
      <button type="button" class="${historyTab === "chats" ? "primary-btn" : "secondary-btn"}" data-action="tab-chats">${s.history.tabChats}</button>
      <button type="button" class="${historyTab === "storyBibles" ? "primary-btn" : "secondary-btn"}" data-action="tab-story-bibles">${s.history.tabStoryBibles}</button>
    </div>
  `;

  // Three flex regions (see .screen.provider-list-screen in taskpane.css): title/back-link and
  // the tab toggle are fixed at the top, #history-tab-content (class="screen-list-scroll") is the
  // one child that actually scrolls, and #history-bulk-bar-slot is fixed at the bottom — moved
  // there from the top per explicit follow-up request ("the selected count, 3 buttons... to be at
  // the bottom and always visible"), no longer wrapped alongside the header/toggle.
  container.innerHTML = `
    <div class="screen provider-list-screen">
      <div class="screen-header">
        <h2>${s.history.title}</h2>
        <button type="button" class="link-btn" data-action="back">${s.common.backToChat}</button>
      </div>
      ${toggleHtml}
      <div id="history-tab-content" class="screen-list-scroll"></div>
      <div id="history-bulk-bar-slot"></div>
    </div>
  `;

  container.querySelector('[data-action="back"]')?.addEventListener("click", handlers.onBackToChat);
  container.querySelector('[data-action="tab-chats"]')?.addEventListener("click", () => {
    historyTab = "chats";
    resetBulkSelectionState();
    renderSessionHistory(container, handlers, null);
  });
  container.querySelector('[data-action="tab-story-bibles"]')?.addEventListener("click", () => {
    historyTab = "storyBibles";
    resetBulkSelectionState();
    renderSessionHistory(container, handlers, null);
  });

  const tabContent = container.querySelector("#history-tab-content") as HTMLElement;
  if (historyTab === "chats") {
    renderClosedChatsTab(tabContent, container, handlers, confirmingPurgeId);
  } else {
    renderDeletedStoryBiblesTab(tabContent, container, handlers, confirmingPurgeId);
  }
}

function renderClosedChatsTab(
  tabContent: HTMLElement,
  container: HTMLElement,
  handlers: SessionHistoryHandlers,
  confirmingPurgeId: string | null
): void {
  const s = getStrings();
  const sessions = getClosedSessions();

  // Rendered into the fixed-bottom slot (#history-bulk-bar-slot, a sibling of #history-tab-content
  // — see renderSessionHistory), not inside tabContent, so it stays pinned below the scrolling
  // list rather than scrolling away with the rest of this tab's content. The .screen-fixed-footer
  // class (border-top divider + spacing, taskpane.css) is applied here only when there's actually
  // something to show — an empty closed-chats list has nothing to bulk-act on, and an empty,
  // bordered strip at the bottom would look like broken chrome rather than an intentionally bare
  // state. See renderDeletedStoryBiblesTab's own copy of this same shape for the Story Bible
  // side — deliberately not shared as one helper despite the near-identical markup, matching this
  // codebase's existing preference for small self-contained rules over shared abstractions (see
  // CLAUDE.md's "In-list markers" section for the same reasoning applied elsewhere).
  const bulkBarSlot = container.querySelector("#history-bulk-bar-slot") as HTMLElement;
  bulkBarSlot.className = sessions.length === 0 ? "" : "screen-fixed-footer";
  bulkBarSlot.innerHTML =
    sessions.length === 0
      ? ""
      : bulkPurgeConfirmChats
        ? `<div class="history-bulk-actions is-confirming">
             <p class="confirm-text">${
               bulkPurgeConfirmChats === "selected"
                 ? s.history.purgeSelectedChatsConfirm(selectedClosedChatIds.size)
                 : s.history.purgeAllChatsConfirm(sessions.length)
             }</p>
             <div class="actions-row">
               <button type="button" class="danger-btn" data-action="confirm-bulk-purge">${s.history.yesPurge}</button>
               <button type="button" class="secondary-btn" data-action="cancel-bulk-purge">${s.common.cancel}</button>
             </div>
           </div>`
        : `<div class="history-bulk-actions">
             <span class="history-bulk-count">${s.history.selectedCount(selectedClosedChatIds.size)}</span>
             <div class="actions-row">
               <button type="button" class="secondary-btn" data-action="bulk-recover"${selectedClosedChatIds.size === 0 ? " disabled" : ""}>${s.history.recoverSelected}</button>
               <button type="button" class="danger-btn" data-action="bulk-purge-selected"${selectedClosedChatIds.size === 0 ? " disabled" : ""}>${s.history.purgeSelected}</button>
               <button type="button" class="danger-btn" data-action="bulk-purge-all">${s.history.purgeAll}</button>
             </div>
           </div>`;

  tabContent.innerHTML =
    sessions.length === 0
      ? `<p class="empty-hint">${s.history.emptyChats}</p>`
      : `<ul class="provider-items">
          ${sessions
            .map((sess) => {
              const isConfirming = confirmingPurgeId === sess.id;
              const provider = getProvider(sess.providerId);
              const providerLabel = provider ? provider.name : s.history.unknownProvider;
              const messageCount = sess.messages.length;
              return `
            <li class="provider-item history-item">
              <input
                type="checkbox"
                class="history-item-checkbox"
                data-id="${sess.id}"
                aria-label="${escapeAttr(s.history.selectItemAria(sess.title))}"
                ${selectedClosedChatIds.has(sess.id) ? "checked" : ""}
              />
              <div class="history-item-body">
                <div class="provider-item-info">
                  <strong>${escapeHtml(sess.title)}</strong>
                  <span class="provider-meta">${escapeHtml(providerLabel)} &middot; ${s.history.messageCount(messageCount)}</span>
                </div>
                <div class="provider-item-actions">
                  ${
                    isConfirming
                      ? `<p class="confirm-text">${s.history.purgeChatConfirm}</p>
                         <div class="actions-row">
                           <button type="button" class="danger-btn" data-action="confirm-purge" data-id="${sess.id}">${s.history.yesPurge}</button>
                           <button type="button" class="secondary-btn" data-action="cancel-purge" data-id="${sess.id}">${s.common.cancel}</button>
                         </div>`
                      : `<div class="actions-row">
                           <button type="button" class="secondary-btn" data-action="export" data-id="${sess.id}">${s.common.export}</button>
                           <button type="button" class="secondary-btn" data-action="recover" data-id="${sess.id}">${s.history.recover}</button>
                           <button type="button" class="danger-btn" data-action="purge" data-id="${sess.id}">${s.history.purge}</button>
                         </div>`
                  }
                </div>
              </div>
            </li>`;
            })
            .join("")}
        </ul>`;

  tabContent.querySelectorAll<HTMLInputElement>(".history-item-checkbox").forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      const id = checkbox.dataset.id;
      if (!id) return;
      if (checkbox.checked) selectedClosedChatIds.add(id);
      else selectedClosedChatIds.delete(id);
      renderSessionHistory(container, handlers, confirmingPurgeId);
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="export"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const session = sessions.find((sess) => sess.id === btn.dataset.id);
      if (!session) return;
      const provider = getProvider(session.providerId);
      exportSessionToFile(session, provider ? provider.name : s.history.unknownProvider);
    });
  });

  // Still calls reopenSession() (storage.ts) — the underlying mechanism is unchanged, only the
  // displayed label and this data-action name moved from "reopen" to "recover" to match Story
  // Bible's own wording (see this file's top-of-file comment).
  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="recover"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.id) {
        reopenSession(btn.dataset.id);
        selectedClosedChatIds.delete(btn.dataset.id);
      }
      handlers.onBackToChat();
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="purge"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      renderSessionHistory(container, handlers, btn.dataset.id || null);
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="cancel-purge"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      renderSessionHistory(container, handlers, null);
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="confirm-purge"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.id) {
        deleteSession(btn.dataset.id);
        selectedClosedChatIds.delete(btn.dataset.id);
      }
      renderSessionHistory(container, handlers, null);
    });
  });

  // Recover selected has no confirmation — matches the single-item Recover above (a positive,
  // reversible action). Both bulk purge buttons below only show the confirm bar; the actual
  // purge happens from confirm-bulk-purge once the author agrees. Queried from bulkBarSlot, not
  // tabContent, now that the bulk bar lives in the sticky header's own slot.
  bulkBarSlot.querySelector('[data-action="bulk-recover"]')?.addEventListener("click", () => {
    if (selectedClosedChatIds.size === 0) return;
    selectedClosedChatIds.forEach((id) => reopenSession(id));
    selectedClosedChatIds.clear();
    handlers.onBackToChat();
  });

  bulkBarSlot
    .querySelector('[data-action="bulk-purge-selected"]')
    ?.addEventListener("click", () => {
      if (selectedClosedChatIds.size === 0) return;
      bulkPurgeConfirmChats = "selected";
      renderSessionHistory(container, handlers, null);
    });

  bulkBarSlot.querySelector('[data-action="bulk-purge-all"]')?.addEventListener("click", () => {
    if (sessions.length === 0) return;
    bulkPurgeConfirmChats = "all";
    renderSessionHistory(container, handlers, null);
  });

  bulkBarSlot.querySelector('[data-action="cancel-bulk-purge"]')?.addEventListener("click", () => {
    bulkPurgeConfirmChats = null;
    renderSessionHistory(container, handlers, null);
  });

  bulkBarSlot.querySelector('[data-action="confirm-bulk-purge"]')?.addEventListener("click", () => {
    const idsToPurge =
      bulkPurgeConfirmChats === "all"
        ? sessions.map((sess) => sess.id)
        : Array.from(selectedClosedChatIds);
    idsToPurge.forEach((id) => deleteSession(id));
    selectedClosedChatIds.clear();
    bulkPurgeConfirmChats = null;
    renderSessionHistory(container, handlers, null);
  });
}

// Recover has no confirmation step (matches Recover on the chats side — a positive, reversible
// action). Purge does (matches chats' own permanent Purge) — it's the one truly destructive
// action on this whole screen.
function renderDeletedStoryBiblesTab(
  tabContent: HTMLElement,
  container: HTMLElement,
  handlers: SessionHistoryHandlers,
  confirmingPurgeId: string | null
): void {
  const s = getStrings();
  const bibles = getDeletedStoryBibles();

  // See renderClosedChatsTab's own copy of this same comment for why this renders into the
  // fixed-bottom slot rather than tabContent, and why the wrapper class is conditional.
  const bulkBarSlot = container.querySelector("#history-bulk-bar-slot") as HTMLElement;
  bulkBarSlot.className = bibles.length === 0 ? "" : "screen-fixed-footer";
  bulkBarSlot.innerHTML =
    bibles.length === 0
      ? ""
      : bulkPurgeConfirmBibles
        ? `<div class="history-bulk-actions is-confirming">
             <p class="confirm-text">${
               bulkPurgeConfirmBibles === "selected"
                 ? s.history.purgeSelectedBiblesConfirm(selectedDeletedBibleIds.size)
                 : s.history.purgeAllBiblesConfirm(bibles.length)
             }</p>
             <div class="actions-row">
               <button type="button" class="danger-btn" data-action="confirm-bulk-purge">${s.history.yesPurge}</button>
               <button type="button" class="secondary-btn" data-action="cancel-bulk-purge">${s.common.cancel}</button>
             </div>
           </div>`
        : `<div class="history-bulk-actions">
             <span class="history-bulk-count">${s.history.selectedCount(selectedDeletedBibleIds.size)}</span>
             <div class="actions-row">
               <button type="button" class="secondary-btn" data-action="bulk-recover"${selectedDeletedBibleIds.size === 0 ? " disabled" : ""}>${s.history.recoverSelected}</button>
               <button type="button" class="danger-btn" data-action="bulk-purge-selected"${selectedDeletedBibleIds.size === 0 ? " disabled" : ""}>${s.history.purgeSelected}</button>
               <button type="button" class="danger-btn" data-action="bulk-purge-all">${s.history.purgeAll}</button>
             </div>
           </div>`;

  tabContent.innerHTML =
    bibles.length === 0
      ? `<p class="empty-hint">${s.history.emptyDeletedBibles}</p>`
      : `<ul class="provider-items">
          ${bibles
            .map((b) => {
              const isConfirming = confirmingPurgeId === b.id;
              return `
            <li class="provider-item history-item">
              <input
                type="checkbox"
                class="history-item-checkbox"
                data-id="${b.id}"
                aria-label="${escapeAttr(s.history.selectItemAria(b.name))}"
                ${selectedDeletedBibleIds.has(b.id) ? "checked" : ""}
              />
              <div class="history-item-body">
                <div class="provider-item-info">
                  <strong>${escapeHtml(b.name)}</strong>
                  <span class="provider-meta">${s.storyBible.entryCount(b.entries.length)}</span>
                </div>
                <div class="provider-item-actions">
                  ${
                    isConfirming
                      ? `<p class="confirm-text">${s.history.purgeConfirm}</p>
                         <div class="actions-row">
                           <button type="button" class="danger-btn" data-action="confirm-purge" data-id="${b.id}">${s.history.yesPurge}</button>
                           <button type="button" class="secondary-btn" data-action="cancel-purge" data-id="${b.id}">${s.common.cancel}</button>
                         </div>`
                      : `<div class="actions-row">
                           <button type="button" class="secondary-btn" data-action="export" data-id="${b.id}">${s.common.export}</button>
                           <button type="button" class="secondary-btn" data-action="recover" data-id="${b.id}">${s.history.recover}</button>
                           <button type="button" class="danger-btn" data-action="purge" data-id="${b.id}">${s.history.purge}</button>
                         </div>`
                  }
                </div>
              </div>
            </li>`;
            })
            .join("")}
        </ul>`;

  tabContent.querySelectorAll<HTMLInputElement>(".history-item-checkbox").forEach((checkbox) => {
    checkbox.addEventListener("change", () => {
      const id = checkbox.dataset.id;
      if (!id) return;
      if (checkbox.checked) selectedDeletedBibleIds.add(id);
      else selectedDeletedBibleIds.delete(id);
      renderSessionHistory(container, handlers, confirmingPurgeId);
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="export"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      const bible = bibles.find((b) => b.id === btn.dataset.id);
      if (bible) exportStoryBibleToFile(bible);
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="recover"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.id) {
        restoreStoryBible(btn.dataset.id);
        selectedDeletedBibleIds.delete(btn.dataset.id);
      }
      renderSessionHistory(container, handlers, null);
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="purge"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      renderSessionHistory(container, handlers, btn.dataset.id || null);
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="cancel-purge"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      renderSessionHistory(container, handlers, null);
    });
  });

  tabContent.querySelectorAll<HTMLButtonElement>('[data-action="confirm-purge"]').forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.id) {
        purgeStoryBible(btn.dataset.id);
        selectedDeletedBibleIds.delete(btn.dataset.id);
      }
      renderSessionHistory(container, handlers, null);
    });
  });

  bulkBarSlot.querySelector('[data-action="bulk-recover"]')?.addEventListener("click", () => {
    if (selectedDeletedBibleIds.size === 0) return;
    selectedDeletedBibleIds.forEach((id) => restoreStoryBible(id));
    selectedDeletedBibleIds.clear();
    renderSessionHistory(container, handlers, null);
  });

  bulkBarSlot
    .querySelector('[data-action="bulk-purge-selected"]')
    ?.addEventListener("click", () => {
      if (selectedDeletedBibleIds.size === 0) return;
      bulkPurgeConfirmBibles = "selected";
      renderSessionHistory(container, handlers, null);
    });

  bulkBarSlot.querySelector('[data-action="bulk-purge-all"]')?.addEventListener("click", () => {
    if (bibles.length === 0) return;
    bulkPurgeConfirmBibles = "all";
    renderSessionHistory(container, handlers, null);
  });

  bulkBarSlot.querySelector('[data-action="cancel-bulk-purge"]')?.addEventListener("click", () => {
    bulkPurgeConfirmBibles = null;
    renderSessionHistory(container, handlers, null);
  });

  bulkBarSlot.querySelector('[data-action="confirm-bulk-purge"]')?.addEventListener("click", () => {
    const idsToPurge =
      bulkPurgeConfirmBibles === "all"
        ? bibles.map((b) => b.id)
        : Array.from(selectedDeletedBibleIds);
    idsToPurge.forEach((id) => purgeStoryBible(id));
    selectedDeletedBibleIds.clear();
    bulkPurgeConfirmBibles = null;
    renderSessionHistory(container, handlers, null);
  });
}
